if (typeof debounce === 'undefined') {
  var debounce = function(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');

      // The drawer must live outside the header: .header-section creates a
      // stacking context (sticky, z-index 50) that sits below the
      // click-capture (52), so a fixed drawer left inside it could never
      // paint above the dim. It is rendered inside the header section for
      // access to the section's settings/blocks, then teleported to
      // #wrapper next to the other side panels. Stale copies from previous
      // section renders (theme editor) are removed first.
      const wrapper = document.getElementById('wrapper');
      if (this.menu && wrapper) {
        document.querySelectorAll('.mobile-menu-drawer').forEach((el) => {
          if (el !== this.menu) el.remove();
        });
        wrapper.appendChild(this.menu);
      }
      this._scrollTicking = false;
      this._stickyOffset = 0;

      // The drawer opens through OverlayManager, which provides the
      // click-capture, scroll lock, focus trap and Escape handling.
      // The hamburger state is synced from the panel events so every
      // close path (capture click, Escape, close button) resets it.
      const toggleButton = this.toggle.querySelector('.mobile-toggle');
      toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        if (OverlayManager.isOpen(this.menu)) {
          OverlayManager.close(this.menu);
        } else {
          // The menu shows its own X only as the desktop drawer, which opens
          // over the header. Otherwise the toggle turns into the X, so it
          // joins the menu's focus trap.
          const close = this.menu.querySelector('.side-panel-close__button');
          const include = close && close.getClientRects().length ? [] : [toggleButton];
          OverlayManager.open(this.menu, { include, givesWay: true });
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });
      // Elements an open submenu covers, made inert, per submenu <details>.
      this.menu.querySelector('.mobile-menu-drawer__close')?.addEventListener('click', () => OverlayManager.close(this.menu));
      this.coveredBySubmenu = new Map();
      document.addEventListener('panel:open', (e) => {
        if (e.detail.panel === this.menu) {
          this.toggle.classList.add('active');
          // Browsers take a <summary>'s expanded state from its <details>,
          // ahead of aria-expanded, so keep both in step.
          this.toggle.setAttribute('open', '');
          toggleButton.setAttribute('aria-expanded', 'true');
          // Start at the top level: Escape and the toggle close the menu
          // with a submenu still open.
          this.menu.querySelectorAll('details[open]').forEach((details) => {
            this.uncoverMenu(details);
            details.classList.remove('menu-opening', 'submenu-open');
            details.removeAttribute('open');
          });
          // Measure the header again as the menu opens. Its position only
          // matters while the drawer is on screen, and anything that shifted
          // the layout since load (the announcement bar marquee collapsing,
          // late fonts, a dismissed bar) would otherwise leave the drawer
          // sitting in a gap below the header.
          this.setHeaderBottom();
        }
      });
      document.addEventListener('panel:closed', (e) => {
        if (e.detail.panel === this.menu) {
          this.toggle.classList.remove('active');
          this.toggle.removeAttribute('open');
          toggleButton.setAttribute('aria-expanded', 'false');
        }
      });

      // Cache sticky offset and recalculate on resize
      this._cacheStickyOffset();
      window.addEventListener('resize', debounce(() => {
        this._cacheStickyOffset();
        this.setHeaderBottom();
      }, 100));

      // Single scroll listener with rAF throttle
      window.addEventListener('scroll', () => {
        if (!this._scrollTicking) {
          this._scrollTicking = true;
          requestAnimationFrame(() => {
            this.setStickyClass();
            this.setHeaderOffset();
            this.setHeaderHeight();
            this.setHeaderBottom();
            this._scrollTicking = false;
          });
        }
      }, { passive: true });

      window.dispatchEvent(new Event('scroll'));

      // Those vars are otherwise only refreshed on scroll, so they keep
      // whatever the layout was at load. The announcement bar marquee
      // collapses shortly after load (and late fonts can resize the header),
      // which left them measuring a taller header than the one on screen —
      // the mobile menu then opened a gap below it. Re-measure whenever the
      // header or the bar actually changes geometry. The bar is observed as
      // well because its collapse moves the header without resizing it.
      if ('ResizeObserver' in window) {
        this._headerResizeObserver = new ResizeObserver(() => {
          this.setHeaderOffset();
          this.setHeaderHeight();
          this.setHeaderBottom();
        });
        this._headerResizeObserver.observe(this);
        const a_bar_section = document.querySelector('.announcement-bar-section');
        if (a_bar_section) {
          this._headerResizeObserver.observe(a_bar_section);
        }
      }

      if (document.querySelector('.announcement-bar-section')) {
        window.addEventListener('scroll', () => this.setAnnouncementHeight(), {
          passive: true
        });
        window.dispatchEvent(new Event('resize'));
      }

      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }

    _cacheStickyOffset() {
      if (this.classList.contains('header-sticky--active')) {
        // Skip recalculation while sticky — the header is position:sticky at top:0,
        // so getBoundingClientRect().top would return 0 and corrupt the offset.
        if (this.classList.contains('is-sticky')) {
          return;
        }
        this._stickyOffset = parseInt(this.header_section.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
      }
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        this.classList.toggle('is-sticky', window.scrollY >= this._stickyOffset && window.scrollY > 0);
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.getElementById('shopify-section-announcement-bar');
      if (a_bar) {
        let h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }
    }
    disconnectedCallback() {
      // The theme editor re-renders the section — without this the old
      // observer keeps firing against a detached header.
      if (this._headerResizeObserver) {
        this._headerResizeObserver.disconnect();
        this._headerResizeObserver = null;
      }
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    // The header's real bottom edge. --header-offset + --header-height only
    // approximates it: clientHeight drops the border, and neither accounts
    // for a transparent header being taken out of flow. Measure it directly
    // so the mobile menu can sit flush under the header. Clamped at 0 so a
    // non-sticky header scrolling away puts the menu at the viewport top.
    setHeaderBottom() {
      let b = this.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--header-bottom', Math.max(0, b) + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentLinksEnabled = this.menu.dataset.parentLinks === 'true';

      // When parent links setting is on, only arrow opens submenu
      if (parentLinksEnabled) {
        event.preventDefault();
        const isArrowClick = event.target.closest('.link-forward');

        if (!isArrowClick) {
          const anchor = summaryElement.querySelector('a');
          if (anchor && anchor.href) {
            window.location.href = anchor.href;
          }
          return;
        }
        // Arrow clicked — manually open details
        detailsElement.setAttribute('open', '');
      }

      const parentMenuElement = detailsElement.closest('.link-container');

      // Stamp deferred template content on first open
      const tpl = detailsElement.querySelector(':scope > template.mobile-menu-deferred');
      if (tpl) {
        tpl.replaceWith(tpl.content.cloneNode(true));
        // Re-bind back buttons for the newly stamped content
        detailsElement.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
        detailsElement.querySelectorAll('details.link-container summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      }

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement?.classList.add('submenu-open');
        this.coverMenu(detailsElement);
      }, 100);
    }
    // The open submenu covers the menu beneath it. Move focus to the
    // submenu's back button and make what it covers inert, out of the tab
    // order and the accessibility tree. Walks up from the submenu to the
    // drawer, so a nested submenu also covers its parent submenu.
    coverMenu(detailsElement) {
      const submenu = detailsElement.querySelector(':scope > .sub-menu');
      const inner = this.menu.querySelector('.mobile-menu-drawer--inner');
      if (!submenu || !inner?.contains(submenu)) return;
      const path = [];
      for (let node = submenu; node !== inner; node = node.parentElement) path.push(node);
      const covered = [];
      path.forEach((node) => {
        Array.from(node.parentElement.children).forEach((sibling) => {
          // The desktop drawer's close band stays above the submenu.
          if (sibling === node || sibling.inert || sibling.classList.contains('mobile-menu-drawer--close')) return;
          covered.push(sibling);
        });
      });
      this.coveredBySubmenu.set(detailsElement, covered);
      // Two frames on: the submenu only turns visible once its slide-in
      // transition is under way (it starts with the next frame's style
      // update), and a hidden button can't take focus. Skipped if the
      // submenu closed again in the meantime.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.coveredBySubmenu.get(detailsElement) !== covered) return;
        submenu.querySelector('.parent-link-back--button')?.focus();
        covered.forEach((el) => {
          el.inert = true;
        });
      }));
    }
    uncoverMenu(detailsElement) {
      this.coveredBySubmenu.get(detailsElement)?.forEach((el) => {
        el.inert = false;
      });
      this.coveredBySubmenu.delete(detailsElement);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
      // Back to the item that opened the submenu, no longer inert.
      detailsElement.querySelector(':scope > summary')?.focus();
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.uncoverMenu(detailsElement);
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    connectedCallback() {
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      if (!this.submenus.length) {
        return;
      }
      // resize on initial load
      window.addEventListener('resize', debounce(() => {
        this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(() => {
        this.resizeSubMenus();
      });
    }
    resizeSubMenus() {
      // Nested sub-menus open on the end side: to the right in LTR, to the
      // left in RTL. .left-submenu moves one to the start side when it would
      // leave the viewport.
      const rtl = document.dir === 'rtl';

      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            parent = sub_submenu.parentElement.parentElement,
            overflows;

          if (rtl) {
            overflows = parent.getBoundingClientRect().left - 10 - w < 0;
          } else {
            let l = parent.getBoundingClientRect().left + parent.clientWidth + 10;
            overflows = w + l > document.body.clientWidth;
          }
          if (overflows) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
